import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { withJWTClient } from "./connectClient";

import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// import.meta.env exists only under Vite; Node (e.g. server importing this graph) has no env object.
// This is for backend-render-brandkit to work.
const viteEnv = (import.meta as ImportMeta & { env?: ImportMetaEnv }).env;

const API_BASE_URL: string =
  viteEnv?.DEV === true
    ? "/offer-bank"
    : (viteEnv?.VITE_OFFER_BANK_API_URL as string) || "";

// --- Types ---

export type CodeSourceType = "static" | "shopify" | "foxy" | "csv";

export type OfferState = "active" | "inactive" | "archived";

export interface Connector {
  platform: string;
  discount_id: string;
  charset: string;
  pattern: string;
}

export interface Offer {
  site_id: string;
  slug: string;
  name: string;
  notes: string;
  type: string;
  state: OfferState;
  code_source_type: CodeSourceType;
  /** read-only */
  available_codes: string;
  /** read-only */
  total_codes: string;
  /** read-only */
  created_at: string;
  /** read-only */
  modified_at: string;
  connector?: Connector;
  static_code: string;
}

export interface OffersListParams {
  states?: OfferState[];
  page?: number;
  pageSize?: number;
  name_search?: string;
}

export interface OffersListResponse {
  offers: Offer[];
  total_offers: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export type CreateOfferPayload = Pick<
  Offer,
  | "name"
  | "slug"
  | "notes"
  | "type"
  | "code_source_type"
  | "static_code"
  | "connector"
>;

export type UpdateOfferPayload = Partial<
  Pick<
    Offer,
    "name" | "notes" | "type" | "static_code" | "code_source_type" | "state"
  > & {
    connector: Partial<Connector>;
    is_static: boolean;
  }
>;

// --- Query Keys ---

export const offerKeys = {
  all: (siteId: string) => ["offers", siteId] as const,
  list: (siteId: string, params?: OffersListParams) =>
    ["offers", siteId, params] as const,
};

// --- API Functions ---

export async function getAllOffers(
  siteId: string,
  { states, page = 1, pageSize = 20 }: OffersListParams = {},
): Promise<OffersListResponse> {
  const params: Record<string, string[] | number> = { page, pageSize };
  if (states?.length) params.states = states;
  const response = await withJWTClient((client) =>
    client.get<OffersListResponse>(`${API_BASE_URL}/${siteId}/offers`, {
      params,
      paramsSerializer: { indexes: null },
    }),
  );
  return response.data;
}

export async function getPaginatedOffers(
  siteId: string,
  { states, page = 1, pageSize = 20, name_search = "" }: OffersListParams = {},
): Promise<OffersListResponse> {
  const params: Record<string, string[] | number | string> = {
    page,
    pageSize,
    name_search: name_search.trim().toLowerCase(),
  };
  if (states?.length) params.states = states;
  const response = await withJWTClient((client) =>
    client.get<OffersListResponse>(
      `${API_BASE_URL}/${siteId}/paginated-offers`,
      {
        params,
        paramsSerializer: { indexes: null },
      },
    ),
  );
  return response.data;
}

export async function createOffer(
  siteId: string,
  data: CreateOfferPayload,
): Promise<Offer> {
  const response = await withJWTClient((client) =>
    client.post<Offer>(`${API_BASE_URL}/${siteId}/offers`, {
      ...data,
      siteId,
    }),
  );
  return response.data;
}

export async function patchOffer(
  siteId: string,
  slug: string,
  data: UpdateOfferPayload,
): Promise<Offer> {
  const response = await withJWTClient((client) =>
    client.patch<Offer>(
      `${API_BASE_URL}/${siteId}/offers/${encodeURIComponent(slug)}`,
      data,
    ),
  );
  return response.data;
}

export async function deleteOffer(siteId: string, slug: string): Promise<void> {
  await withJWTClient((client) =>
    client.delete(
      `${API_BASE_URL}/${siteId}/offers/${encodeURIComponent(slug)}`,
    ),
  );
}

export async function uploadOfferCsv(
  siteId: string,
  offerSlug: string,
  file: File,
): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);

  await withJWTClient((client) =>
    client.post(
      `${API_BASE_URL}/${siteId}/offers/${encodeURIComponent(offerSlug)}/upload-csv`,
      formData,
    ),
  );
}

// --- Query Options ---

export const offersQueryOptions = (
  siteId: string,
  params?: OffersListParams,
) => ({
  queryKey: offerKeys.list(siteId, params),
  queryFn: () => getAllOffers(siteId, params),
  enabled: !!siteId,
});

export const paginatedOffersQueryOptions = (
  siteId: string,
  params?: OffersListParams,
) => ({
  queryKey: offerKeys.list(siteId, params),
  queryFn: () => getPaginatedOffers(siteId, params),
  enabled: !!siteId,
});
// --- Tanstack Query Hooks ---

export function useOffers(siteId: string, params?: OffersListParams) {
  return useQuery(offersQueryOptions(siteId, params));
}

export function usePaginatedOffers(siteId: string, params?: OffersListParams) {
  return useQuery(paginatedOffersQueryOptions(siteId, params));
}

export function useCreateOffer(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createOffer", siteId],
    mutationFn: (data: CreateOfferPayload) => createOffer(siteId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all(siteId),
      });
      enqueueSnackbar("Offer created successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to create offer");
    },
  });
}

export interface CreateOfferWithCsvPayload {
  offer: CreateOfferPayload;
  file: File;
}

export function useUploadOfferCsv(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["uploadOfferCsv", siteId],
    mutationFn: ({ offerSlug, file }: { offerSlug: string; file: File }) =>
      uploadOfferCsv(siteId, offerSlug, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all(siteId),
      });
      enqueueSnackbar("Coupon codes uploaded successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to upload coupon codes");
    },
  });
}

export function useCreateOfferWithCsv(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createOfferWithCsv", siteId],
    mutationFn: async (data: CreateOfferWithCsvPayload) => {
      const offer = await createOffer(siteId, data.offer);
      await uploadOfferCsv(siteId, offer.slug, data.file);
      return offer;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all(siteId),
      });
      enqueueSnackbar("Offer created successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to create offer");
    },
  });
}

export function useUpdateOfferStatus(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateOfferStatus", siteId],
    mutationFn: ({ slug, state }: { slug: string; state: OfferState }) =>
      patchOffer(siteId, slug, { state }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all(siteId),
      });
      enqueueSnackbar("Offer status updated", { variant: "success" });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update offer status");
    },
  });
}

export function useUpdateOffer(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateOffer", siteId],
    mutationFn: ({ slug, data }: { slug: string; data: UpdateOfferPayload }) =>
      patchOffer(siteId, slug, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all(siteId),
      });
      enqueueSnackbar("Offer updated successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update offer");
    },
  });
}

export function useDeleteOffer(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteOffer", siteId],
    mutationFn: (slug: string) => deleteOffer(siteId, slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all(siteId),
      });
      enqueueSnackbar("Offer deleted successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to delete offer");
    },
  });
}
